// @ts-check

import { CloseOutlined, EyeFilled, EyeOutlined, FilterOutlined, PlusOutlined, HolderOutlined, OrderedListOutlined } from "@ant-design/icons";
import * as turf from "@turf/turf";
import { Badge, Button, Card, Col, Divider, Empty, List, Modal, Row, Select, Space, Switch, Tag, Tooltip, Typography, notification } from "antd";
import axios from "axios";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import L from "leaflet";
import "leaflet-draw";
import { Children, createContext, isValidElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BsFillPinMapFill } from "react-icons/bs";
import { FaBatteryEmpty, FaBatteryQuarter, FaBatteryThreeQuarters, FaCloudRain, FaPowerOff, FaSun, FaTint, FaWind } from "react-icons/fa";
import { IoCheckmarkSharp, IoCloseSharp } from "react-icons/io5";
import { RiDragMove2Line } from "react-icons/ri";
import {
  FeatureGroup as FeatureGroupRL,
  GeoJSON as GeoJSONRL,
  LayerGroup as LayerGroupRL,
  LayersControl as LayersControlRL,
  Marker as MarkerRL,
  Polygon as PolygonRL,
  Popup as PopupRL,
  TileLayer as TileLayerRL,
  useMap,
  useMapEvents,
  useMap as useMapRL,
  ZoomControl as ZoomControlRL,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { deviceModelPulsingPin } from "../components/MarkerIcon/MarkerIcon";
import tr from "../i18n";
import CustomControlComponent from "../leaflet/custom-control-component";
import "../leaflet/leaflet-locale-tr";
import { formatUtcWithOffset } from "../utils/DateFormatter";
import { BASE_MAPS } from "../utils/MapUtils";
import useWindowSize from "../utils/WindowSize";
import { deviceImageMap, DeviceProvider, useDevice } from "./device-context";
import { getGardenAreaColor, getGardenBorderColor, useGarden } from "./garden-context";
import { OptimizationProvider } from "./optimization-context";
import { hasAccess, hasRoles, ROLES } from "../utils/AccessLevels";
import { AccessGate } from "../components/AccessGate";

/**
 * @param {Object} properties
 * @param {import("../types/global").Device} properties.device
 * @returns
 */
// eslint-disable-next-line no-unused-vars
function getModelIcon({ device }) {
  return new L.DivIcon({
    html: `<img alt="${device.deviceSerial}" style="height:38px; width:38px;" src="${deviceImageMap[device.model]}" />`,
    className: "device-location-marker",
    iconSize: [38, 38],
    iconAnchor: [19, 0],
  });
}

/**
 * @param {Object} properties
 * @param {import("../types/global").Device} properties.device
 * @param {boolean} [properties.isDraggable]
 * @returns
 */
function getDeviceLocationIcon({ device, isDraggable = false }) {
  return deviceModelPulsingPin({ alive: device.alive, model: device.model, isDraggable, isWatering: device.isWatering });
}

/**
 * Gets safe bounds of a Leaflet layer
 * @param {L.Layer} layer
 * @returns {L.LatLngBounds | undefined}
 */
function getLayerBounds(layer) {
  if (!layer) return undefined;
  if (layer instanceof L.Marker) {
    return L.latLngBounds([layer.getLatLng()]);
  }

  if ("getBounds" in layer && typeof layer.getBounds === "function") {
    const bounds = layer.getBounds();
    return boundsHaveArea(bounds) ? bounds : undefined;
  }

  if (layer instanceof L.LayerGroup) {
    /** @type {L.LatLng[]} */
    const latLngs = [];

    layer.eachLayer((l) => {
      const childBounds = getLayerBounds(l);
      if (childBounds) {
        latLngs.push(childBounds.getNorthEast(), childBounds.getSouthWest());
      }
    });

    return latLngs.length ? L.latLngBounds(latLngs) : undefined;
  }

  return undefined;
}

/**
 * Checks whether bounds have a real area (not a single point)
 * @param {L.LatLngBounds} bounds
 * @returns {boolean}
 */
function boundsHaveArea(bounds) {
  if (!bounds || !bounds.isValid()) return false;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  return ne.lat !== sw.lat || ne.lng !== sw.lng;
}

/**
 * Checks whether a GeoJSON Polygon or MultiPolygon has valid area
 * @param {import("geojson").Geometry} geometry
 * @returns {boolean}
 */
function isValidPolygonGeometry(geometry) {
  if (!geometry) return false;

  const rings = geometry.type === "Polygon" ? geometry.coordinates : geometry.type === "MultiPolygon" ? geometry.coordinates.flat() : [];

  return rings.some((ring) => {
    if (!Array.isArray(ring) || ring.length < 4) return false;

    const uniquePoints = new Set(ring.map(([lng, lat]) => `${lng},${lat}`));

    return uniquePoints.size >= 3;
  });
}

/**
 * Checks whether a GeoJSON Feature has safe geometry for Leaflet
 * @param {import("geojson").Feature} feature
 * @returns {boolean}
 */
function isRenderableGeoJSONFeature(feature) {
  if (!feature || feature.type !== "Feature") return false;

  const geom = feature.geometry;
  if (!geom || typeof geom !== "object") return false;

  if (geom.type !== "Polygon" && geom.type !== "MultiPolygon") return false;

  if (!Array.isArray(geom.coordinates)) return false;

  // Polygon → coordinates: Position[][]
  // MultiPolygon → coordinates: Position[][][]
  if (geom.type === "Polygon" && geom.coordinates.length === 0) return false;
  if (geom.type === "MultiPolygon" && geom.coordinates.length === 0) return false;

  return isValidPolygonGeometry(geom);
}

/**
 * Converts GeoJSON Polygon/MultiPolygon geometry to React-Leaflet Polygon positions.
 * @param {import("geojson").Polygon | import("geojson").MultiPolygon} geometry
 * @returns {import("leaflet").LatLngExpression[][] | import("leaflet").LatLngExpression[][][]}
 */
function geoJSONPolygonToLeafletPositions(geometry) {
  if (geometry.type === "Polygon") {
    return geometry.coordinates.map((ring) => ring.map(([lng, lat]) => [lat, lng]));
  }

  return geometry.coordinates.map((polygon) => polygon.map((ring) => ring.map(([lng, lat]) => [lat, lng])));
}

const VALVE_GROUP_BASE_COLORS = [
  "#1D4ED8", // blue
  "#DC2626", // red
  "#059669", // emerald
  "#7C3AED", // violet
  "#D97706", // amber
  "#DB2777", // pink
  "#0891B2", // cyan
  "#65A30D", // lime
  "#EA580C", // orange
  "#4338CA", // indigo
  "#0F766E", // teal
  "#E11D48", // rose
];

/**
 * Maps a valve/group id to a deterministic palette index.
 * @param {string | number} id
 * @returns {number}
 */
function getValveGroupColorIndex(id) {
  const idAsString = String(id);

  if (/^\d+$/.test(idAsString)) {
    const numericId = Number(idAsString);
    return Math.abs(numericId - 1) % VALVE_GROUP_BASE_COLORS.length;
  }

  let hash = 0;
  for (let index = 0; index < idAsString.length; index += 1) {
    hash = (hash * 31 + idAsString.charCodeAt(index)) >>> 0;
  }

  return hash % VALVE_GROUP_BASE_COLORS.length;
}

/**
 * Returns valve fill/border color by valve/group id.
 * @param {string | number} id
 * @param {string | number} activeValveId
 * @returns {{ fillColor: string; color: string }}
 */
export function getValveGroupColors(id, activeValveId) {
  const baseColor = VALVE_GROUP_BASE_COLORS[getValveGroupColorIndex(id)];
  const isActive = String(activeValveId) === String(id);

  return {
    fillColor: `${baseColor}${isActive ? "88" : "65"}`,
    color: isActive ? baseColor : `${baseColor}c6`,
  };
}

const MapsContext = /** @type {import("react").Context<import("../types/global").MapsContextValue | undefined>} */ (createContext(undefined));

export function useMaps() {
  const context = /** @type {import("../types/global").MapsContextValue | undefined} */ (useContext(MapsContext));
  if (!context) throw new Error("useMaps must be used within a MapsProvider");
  return context;
}

/**
 * @param {{
 *   enabled: boolean;
 *   onSelect: (latlng: { lat: number; lng: number }) => void;
 * }} props
 */
export function MapClickLocationPicker({ enabled, onSelect }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onSelect(e.latlng);
    },
  });

  return null;
}

export function LocationEditCursor({ enabled }) {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    if (enabled) {
      container.classList.add("location-edit-mode");
    } else {
      container.classList.remove("location-edit-mode");
    }

    return () => {
      container.classList.remove("location-edit-mode");
    };
  }, [enabled, map]);

  return null;
}

export function MapsProvider(/** @type {import("react").PropsWithChildren<{}>} */ { children }) {
  const [activeValveId, setActiveValveId] = useState(/** @type {string | null} */(null));
  const [baseMapLabel, setBaseMapLabel] = /** @type {import("../types/global").MapsContextValue["baseMapLabelState"]} */ (useState(BASE_MAPS[0].label));
  const [gardenForMap, setGardenForMap] = useState(/** @type {import("../types/global").Garden | undefined} */(undefined));
  const map = useMapRL();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isGardensSelectListOpen, setIsGardensSelectListOpen] = useState(true);
  const [searchParameters] = useSearchParams();
  const { width: windowWidth } = useWindowSize();

  const { data: tags, isFetching: isFetchingTags } = useQuery({
    queryKey: ["Tags"],
    queryFn: () =>
      axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/tag`).then(
        (
          /** @type {import("axios").AxiosResponse<import("../types/global").GetTagsApiResponse>} */
          res,
        ) => res.data.tags,
      ),
  });

  const { mutate: mutateUpdateDeviceLocation } = useMutation(
    /** @param {import("../types/global").UpdateDeviceLocationRequest} params */
    ({ deviceId, lat, lng }) => axios.put(`${process.env.REACT_APP_GARDENER_API_END_POINT}/device/location/${deviceId}`, { lat, lng }),
  );

  const fitBoundsToCheckedOverlays = useCallback(() => {
    try {
      if (!map) return;

      /** @type {L.LatLng[]} */
      const allLatLngs = [];

      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) return;

        const bounds = getLayerBounds(layer);
        if (bounds) {
          allLatLngs.push(bounds.getNorthEast(), bounds.getSouthWest());
        }
      });

      if (allLatLngs.length === 0) return;

      const uniqueCoords = allLatLngs.filter((coord, index, self) => index === self.findIndex((c) => c.lat === coord.lat && c.lng === coord.lng));

      if (uniqueCoords.length === 1) {
        console.count("fitBoundsToCheckedOverlays - single coord");
        map.setView(uniqueCoords[0], 18);
      } else {
        console.count("fitBoundsToCheckedOverlays - multiple coords");
      }
      map.invalidateSize();
    } catch (e) {
      return;
    }
  }, [isFilterExpanded, map, windowWidth]);

  const fitBoundsToGardens = useCallback(
    /** @type {import("../types/global").fitBoundsToGardensFunction} */
    ({ gardens, map }) => {
      if (!gardens || !map) return;

      /** @type {L.LatLng[]} */
      const allLatLngs = [];

      gardens.forEach((garden) => {
        if (!garden.geoJson) return;

        const flattened = turf.flatten(garden.geoJson);

        flattened.features.forEach((feature) => {
          if (!isRenderableGeoJSONFeature(feature)) return;

          const layer = L.geoJSON(feature);
          layer.eachLayer((l) => {
            const bounds = getLayerBounds(l);
            if (bounds) {
              allLatLngs.push(bounds.getNorthEast(), bounds.getSouthWest());
            }
          });
        });
      });

      if (allLatLngs.length === 0) return;

      const uniqueCoords = allLatLngs.filter((coord, index, self) => index === self.findIndex((c) => c.lat === coord.lat && c.lng === coord.lng));

      if (uniqueCoords.length === 1) {
        console.count("fitBoundsToGardens - single coord");
        map.setView(uniqueCoords[0], 100, { animate: true });
      } else {
        console.count("fitBoundsToGardens - multiple coords");
        map.flyToBounds(L.latLngBounds(allLatLngs), {
          animate: true,
          duration: 1,
          paddingTopLeft: [windowWidth && windowWidth >= 768 ? 340 : 0, isFilterExpanded ? 240 : 80],
        });
      }

      map.invalidateSize();
    },
    [isFilterExpanded, windowWidth],
  );

  useEffect(() => {
    if (!map) return;

    const batteryParam = searchParameters.get("batteryPercentage");
    const rawModels = searchParameters.get("models");
    const selectedModels = rawModels ? rawModels.split(",") : [];

    map.eachLayer((layer) => {
      if (!(layer instanceof L.FeatureGroup || layer instanceof L.LayerGroup)) return;

      layer.eachLayer((marker) => {
        if (!(marker instanceof L.Marker)) return;

        const battery = marker.options.batteryPercentage || 0;
        const model = marker.options.model;
        const alive = marker.options.alive;

        const requiresAliveCheck = model === "LT";
        const isAliveOk = !requiresAliveCheck || alive === "alive";
        const hasBattery = !model ? false : ["BL", "LT"].includes(model);

        // --- Battery filtering -------------------------------------
        const batteryOk =
          !batteryParam ||
          batteryParam === "all" ||
          (batteryParam === "half" && battery >= 50 && hasBattery && isAliveOk) ||
          (batteryParam === "quarter" && battery >= 25 && battery < 50 && hasBattery && isAliveOk) ||
          (batteryParam === "lt-quarter" && battery < 25 && hasBattery && isAliveOk) ||
          (batteryParam === "empty" && hasBattery && battery < 1);

        // --- Model filtering ----------------------------------------
        const modelOk =
          selectedModels.length === 0 || // no filter => accept all
          (model && selectedModels.includes(model));

        // --- Final decision -----------------------------------------
        const show = batteryOk && modelOk;

        show ? marker.addTo(map) : map.removeLayer(marker);
      });
    });
  }, [map, searchParameters]);

  useEffect(() => {
    const value = searchParameters.get("selectedGardenId");

    if (!map) return;
    if (!value) return;

    map.eachLayer((layer) => {
      if (layer instanceof L.FeatureGroup && layer.options.type === "garden") {
        if (layer.options.id === value) {
          const bounds = layer.getBounds();

          /**
           * @deprecated
           * Disabled for testing purposes.
           * fitBoundsToCheckedOverlays and fitBoundsToGardens are both invoked in
           * sub-composition items' useEffects, which caused repetitive fit-bounds
           * behavior during testing, so this block is intentionally commented out.
           */
          // if (boundsHaveArea(bounds)) {
          //   /** @type {import("leaflet").FitBoundsOptions} */
          //   const options = {
          //     animate: true,
          //     duration: 1,
          //     maxZoom: 18,
          //     paddingTopLeft: [windowWidth && windowWidth >= 768 ? 340 : 0, isFilterExpanded ? 240 : 80],
          //   };

          //   map.flyToBounds(bounds, options);
          // }
        }
      }
    });
  }, [isFilterExpanded, map, searchParameters, windowWidth]);

  const mapsContextValue = useMemo(() => {
    /** @type {import("../types/global").MapsContextValue} */
    const value = {
      activeValveIdState: [activeValveId, setActiveValveId],
      baseMapLabelState: [baseMapLabel, setBaseMapLabel],
      fitBoundsToCheckedOverlays,
      fitBoundsToGardens,
      getValveGroupColors,
      gardenForMapState: [gardenForMap, setGardenForMap],
      isFilterExpandedState: [isFilterExpanded, setIsFilterExpanded],
      isGardensSelectListOpenState: [isGardensSelectListOpen, setIsGardensSelectListOpen],
      tags,
      isFetchingTags,
      mutateUpdateDeviceLocation,
      setActiveValveId,
    };

    return value;
  }, [
    activeValveId,
    baseMapLabel,
    fitBoundsToCheckedOverlays,
    fitBoundsToGardens,
    gardenForMap,
    isFilterExpanded,
    isGardensSelectListOpen,
    tags,
    isFetchingTags,
    mutateUpdateDeviceLocation,
  ]);

  if (process.env.NODE_ENV === "development") {
    window.map = map;
    window.mapsContextValue = mapsContextValue;
  }

  return <MapsContext.Provider value={mapsContextValue}>{children}</MapsContext.Provider>;
}

MapsProvider.AddGardenArea = function MapsProviderAddGardenArea(/** @type {{callback: (feature: import("geojson").Feature) => void }} */ { callback }) {
  const map = useMapRL();
  const featureGroupRef = /** @type {import("react").MutableRefObject<L.FeatureGroup | null>} */ (useRef(null));

  const handlePolygonEvent = useCallback(
    (/** @type {import("react").MutableRefObject<L.FeatureGroup | null>} */ featureGroupRef) => {
      return () => {
        if (!featureGroupRef.current) return;

        const featureCollectionOfPolygons = featureGroupRef.current.toGeoJSON();

        if (featureCollectionOfPolygons.type === "GeometryCollection") {
          console.warn("GeometryCollection is not supported for garden area. Please draw polygons only.");
          return;
        }

        /** @type {import("geojson").FeatureCollection<import("geojson").Polygon | import("geojson").MultiPolygon>} */
        let featureCollection;

        if (featureCollectionOfPolygons.type === "Feature") {
          // If it's a single feature, wrap it in a collection
          const polygonFeature = /** @type {import("geojson").Feature<import("geojson").Polygon | import("geojson").MultiPolygon>} */ (
            /** @type {unknown} */ (featureCollectionOfPolygons)
          );
          featureCollection = turf.featureCollection([polygonFeature]);
        } else {
          // Cast the generic FeatureCollection to the specific one turf expects
          featureCollection = /** @type {import("geojson").FeatureCollection<import("geojson").Polygon | import("geojson").MultiPolygon>} */ (
            featureCollectionOfPolygons
          );
        }

        const combined = turf.combine(featureCollection);
        const geoJson = combined.features[0];

        if (!geoJson) {
          console.error("No features found in the drawn area.");
          alert(tr("No features found in the drawn area. Please draw a valid polygon."));
        }

        callback(geoJson);
      };
    },
    [callback],
  );

  useEffect(() => {
    if (!map) return;

    map.whenReady(() => {
      map.invalidateSize();
    });
  }, [map]);

  return (
    <FeatureGroupRL ref={featureGroupRef}>
      <EditControl
        draw={{ circle: false, circlemarker: false, marker: false, polyline: false, polygon: true, rectangle: false }}
        onCreated={handlePolygonEvent(featureGroupRef)}
        onDeleted={handlePolygonEvent(featureGroupRef)}
        onEdited={handlePolygonEvent(featureGroupRef)}
        position="topleft"
      />
    </FeatureGroupRL>
  );
};

MapsProvider.Container = function MapsProviderContainer(
  /** @type {import("react").PropsWithChildren<{}>} */
  { children },
) {
  const [baseMapLabel] = useMaps().baseMapLabelState;

  return (
    <>
      <MapsProvider.TileLayer defaultBaseMapLabel={baseMapLabel} />
      <MapsProvider.SelectBaseMap position="bottomleft" width={150} />
      <MapsProvider.GardensAreas />
      <MapsProvider.GardenOrderModal />
      <ZoomControlRL position="bottomright" />
      {children}
    </>
  );
};

MapsProvider.DevicePopup = function MapsProviderDevicePopup(/** @type {{ device: import("../types/global").Device }} */ { device }) {
  const valves = device.valves || {};
  const valveCount = device?.valveCount || 8;
  const valveKeys = Object.keys(valves);
  const { sun, rain, wind, active } = device.optimization || {};
  const [, setSearchParameters] = useSearchParams();
  const { gardens } = useGarden();
  const navigate = useNavigate();

  return (
    <div style={{ minWidth: 250 }}>
      <Space align="start" style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
        <Space>
          <div style={{ position: "relative", display: "inline-block" }}>
            <img
              src={deviceImageMap[device.model]}
              alt={device.model}
              style={{
                width: 32,
                height: 32,
                objectFit: "contain",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ lineHeight: 1.2 }}>
              {device.name || device.deviceSerial}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {device.model} • {device.deviceSerial}
            </Typography.Text>
          </div>
        </Space>
      </Space>

      <Divider style={{ margin: "8px 0" }} />

      <Row align="middle" justify="space-between" style={{ marginBottom: 8 }}>
        <Col>
          <Space size={12}>
            <DeviceProvider.DeviceStatus device={device} />
            <DeviceProvider.DeviceBattery device={device} />
            <DeviceProvider.DeviceSignal device={device} />
          </Space>
        </Col>
        <Col>
          <Space>
            <Tooltip title={tr("sun")}>
              <FaSun size={16} style={{ color: active && sun ? "orange" : "lightgray" }} />
            </Tooltip>
            <Tooltip title={tr("rain")}>
              <FaCloudRain size={16} style={{ color: active && rain ? "lightskyblue" : "lightgray" }} />
            </Tooltip>
            <Tooltip title={tr("wind")}>
              <FaWind size={16} style={{ color: active && wind ? "blueviolet" : "lightgray" }} />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {valveCount > 0 && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <div style={{ maxHeight: 150, overflowY: "auto" }}>
            <List
              size="small"
              dataSource={valveKeys.slice(0, valveCount)}
              renderItem={(key) => {
                const valve = valves[key];
                const isOn = valve.state === "on";
                return (
                  <List.Item style={{ padding: "4px 0" }}>
                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                      <Typography.Text style={{ fontSize: 12 }}>{valve.name || `${tr("Zone")} ${key}`}</Typography.Text>
                      {isOn && (
                        <Tag color="processing" style={{ margin: 0, fontSize: 10, lineHeight: "16px" }}>
                          {tr("On")}
                        </Tag>
                      )}
                    </Space>
                  </List.Item>
                );
              }}
            />
          </div>
        </>
      )}

      <Row style={{ marginTop: 8 }}>
        <Col span={12}>
          <Space>
            <Button
              icon={<EyeFilled />}
              onClick={() => {
                /** @type {import("react-router-dom").URLSearchParamsInit} */
                const nextInit = { device_id: device.id, show_map: "true" };
                setSearchParameters(nextInit);
              }}
              shape="circle"
              size="small"
              type="primary"
            />

            <Button
              icon={<BsFillPinMapFill />}
              onClick={() => {
                const garden = gardens?.find((g) => g.devices?.some((d) => d.id === device.id));
                navigate(`/control-center/gardens/${garden?.id}`);
              }}
              shape="circle"
              size="small"
              style={{ alignItems: "center", display: "flex", justifyContent: "center" }}
              type="primary"
            />
          </Space>
        </Col>

        {device.lastOnlineAt && (
          <Col span={12} style={{ textAlign: "right" }}>
            <Tooltip title={tr("Last Connection Time")}>
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {formatUtcWithOffset(device.lastOnlineAt, device.timezoneOffset, device.timezone)}
              </Typography.Text>
            </Tooltip>
          </Col>
        )}
      </Row>
    </div>
  );
};

MapsProvider.DeviceMarker = function MapsProviderDevice(
  /** @type {import("react").PropsWithChildren<{ checked?: boolean; device: import("../types/global").Device; draggable?: import("react-leaflet").MarkerProps["draggable"] }>} */
  { checked = true, device, draggable = false },
) {
  const [isDraggable, setIsDraggable] = useState(draggable);
  const [deviceLocation, setDeviceLocation] = useState(/** @type {import("../types/device").Device["location"] | undefined} */(device.location));
  const { mutateUpdateDeviceLocation } = useMaps();

  useEffect(() => {
    setDeviceLocation(device.location);
  }, [device.location]);

  return (
    <LayersControlRL.Overlay checked={checked} name={tr("Device Location")}>
      <MapClickLocationPicker
        enabled={isDraggable}
        onSelect={({ lat, lng }) => {
          setDeviceLocation({ lat, lng });
        }}
      />

      <LocationEditCursor enabled={isDraggable} />

      {typeof deviceLocation?.lat === "number" && typeof deviceLocation?.lng === "number" && (
        <MarkerRL
          draggable={isDraggable}
          eventHandlers={{
            // dragging still works if you want to keep it
            dragend: (e) => {
              const position = e.target.getLatLng();
              setDeviceLocation({ lat: position.lat, lng: position.lng });
            },
          }}
          icon={getDeviceLocationIcon({ device, isDraggable })}
          position={[deviceLocation.lat, deviceLocation.lng]}
        >
          <PopupRL>
            <MapsProvider.DevicePopup device={device} />
          </PopupRL>
        </MarkerRL>
      )}

      <CustomControlComponent position="topright">
        {isDraggable ? (
          <Space size={5}>
            <Tooltip title={tr("Cancel Location Update")}>
              <Button
                className="icon-circle-btn"
                danger
                icon={<IoCloseSharp />}
                onClick={() => {
                  setIsDraggable(false);
                  setDeviceLocation(device.location);
                }}
                shape="circle"
                size="small"
                type="primary"
              />
            </Tooltip>

            <Tooltip title={tr("Save Device Location")}>
              <Button
                className="icon-circle-btn"
                icon={<IoCheckmarkSharp />}
                onClick={() => {
                  mutateUpdateDeviceLocation(
                    {
                      deviceId: device.id,
                      lat: deviceLocation.lat,
                      lng: deviceLocation.lng,
                    },
                    {
                      onSuccess: () => setIsDraggable(false),
                    },
                  );
                }}
                shape="circle"
                size="small"
                type="primary"
              />
            </Tooltip>
          </Space>
        ) : (
          <Tooltip title={tr("Update Device Location")}>
            <Button className="icon-circle-btn" icon={<RiDragMove2Line size={20} />} onClick={() => setIsDraggable(true)} shape="circle" size="small" type="primary" />
          </Tooltip>
        )}
      </CustomControlComponent>
    </LayersControlRL.Overlay>
  );
};

MapsProvider.DeviceOtherMarkers = function MapsProviderDeviceGardenOtherMarkers(
  /** @type {{ checked?: boolean; otherDevices: import("../types/global").Device[] }} */
  { checked = true, otherDevices },
) {
  return (
    <LayersControlRL.Overlay checked={checked} name={tr("Other Devices")}>
      <LayerGroupRL>
        {otherDevices.map((otherDevice) => {
          if (!otherDevice.location) return null;

          if (typeof otherDevice.location?.lat === "number" && typeof otherDevice.location?.lng === "number") {
            return (
              <MarkerRL icon={getDeviceLocationIcon({ device: otherDevice })} key={otherDevice.id} position={[otherDevice.location.lat, otherDevice.location.lng]}>
                <PopupRL>
                  <MapsProvider.DevicePopup device={otherDevice} />
                </PopupRL>
              </MarkerRL>
            );
          } else {
            return null;
          }
        })}
      </LayerGroupRL>
    </LayersControlRL.Overlay>
  );
};

MapsProvider.FiltersCard = function MapsProviderFiltersCard(/** @type {import("react").PropsWithChildren<{}>} */ { children }) {
  const [isExpanded] = useMaps().isFilterExpandedState;
  const [searchParameters, setSearchParameters] = useSearchParams();

  if (!isExpanded) return null;

  return (
    <>
      <Col style={{ width: 520 }}>
        <Card bodyStyle={{ borderColor: "lightgray", borderWidth: 1, padding: 10 }}>
          <Card.Meta
            style={{ marginBottom: 10 }}
            title={
              <Space align="baseline" size={[5, 0]}>
                <Typography.Title level={5} style={{ marginBottom: 0 }}>
                  {tr("Filters")}
                </Typography.Title>

                {(!!searchParameters.get("tags")?.length || !!searchParameters.get("batteryPercentage")?.length) && (
                  <Button
                    danger
                    onClick={() => {
                      setSearchParameters((prev) => {
                        prev.delete("tags");
                        prev.delete("batteryPercentage");
                        prev.delete("models");
                        prev.delete("selectedGardenId");
                        return prev;
                      });
                    }}
                    size="small"
                    type="link"
                  >
                    {tr("Clear")}
                  </Button>
                )}
              </Space>
            }
          />

          <Row gutter={[10, 10]}>{children}</Row>
        </Card>
      </Col>

      <Col flex="48px">
        <MapsProvider.ShrinkFilterIconButton />
      </Col>
    </>
  );
};

MapsProvider.FilterDevicesByBattery = function MapsProviderFilterDevicesByBattery(
  /** @type {{ selectProps?: import("antd/lib/select").SelectProps<import("../types/global").Garden["id"]>; width?: import("react").CSSProperties["width"] }} */
  { width = "100%", selectProps },
) {
  // const map = useMapRL();
  const { devices } = useDevice();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const hasBattery = (/** @type {import("../types/global").Device} */ d) => ["BL", "LT"].includes(d.model);
  const requiresAliveCheck = (/** @type {import("../types/global").Device} */ d) => d.model === "LT";
  const isAliveOk = (/** @type {import("../types/global").Device} */ d) => !requiresAliveCheck(d) || d.alive === "alive";

  return (
    <Col span={8}>
      <Select
        allowClear
        dropdownMatchSelectWidth={false}
        onChange={(value) => {
          setSearchParameters((prev) => {
            if (!value) {
              prev.delete("batteryPercentage");
              return prev;
            }

            prev.set("batteryPercentage", value);
            return prev;
          });
        }}
        placeholder={tr("Device Battery Filter")}
        style={{ width }}
        {...selectProps}
        value={!!searchParameters.get("batteryPercentage") ? searchParameters.get("batteryPercentage") : undefined}
      >
        <Select.Option value="all">
          <Space>
            <FaBatteryThreeQuarters />

            <Typography.Text>{tr("All Devices")}</Typography.Text>
          </Space>
        </Select.Option>

        <Select.Option value="half">
          <Space>
            <FaBatteryThreeQuarters color="limegreen" size={16} />

            <Typography.Text>
              {"> "}
              {Intl.NumberFormat("tr-tr", { style: "percent", maximumFractionDigits: 0 }).format(0.5)}
            </Typography.Text>

            {devices && (
              <Typography.Text type="secondary">{`(${devices.filter((d) => isAliveOk(d) && hasBattery(d) && d.batteryPct !== undefined && d.batteryPct >= 50).length
                } ${tr("Devices_PL")})`}</Typography.Text>
            )}
          </Space>
        </Select.Option>

        <Select.Option value="quarter">
          <Space>
            <FaBatteryQuarter color="orangered" size={16} />

            <Typography.Text>
              {Intl.NumberFormat("tr-tr", { style: "percent", maximumFractionDigits: 0 }).format(0.5)}
              {"-"}
              {Intl.NumberFormat("tr-tr", { style: "percent", maximumFractionDigits: 0 }).format(0.25)}
            </Typography.Text>

            {devices && (
              <Typography.Text type="secondary">{`(${devices.filter((d) => isAliveOk(d) && hasBattery(d) && d.batteryPct !== undefined && d.batteryPct >= 25 && d.batteryPct < 50).length
                } ${tr("Devices_PL")})`}</Typography.Text>
            )}
          </Space>
        </Select.Option>

        <Select.Option value="lt-quarter">
          <Space>
            <FaBatteryEmpty color="orangered" size={16} />

            <Typography.Text>
              {"< "}
              {Intl.NumberFormat("tr-tr", { style: "percent", maximumFractionDigits: 0 }).format(0.25)}
            </Typography.Text>

            {devices && (
              <Typography.Text type="secondary">{`(${devices.filter((d) => isAliveOk(d) && hasBattery(d) && d.batteryPct !== undefined && d.batteryPct < 25).length
                } ${tr("Devices_PL")})`}</Typography.Text>
            )}
          </Space>
        </Select.Option>

        <Select.Option value="empty">
          <Space>
            <FaPowerOff color="red" size={12} />

            <Typography.Text>{tr("Empty Battery Devices")}</Typography.Text>

            {devices && (
              <Typography.Text type="secondary">{`(${devices.filter((d) => hasBattery(d) && d.batteryPct !== undefined && d.batteryPct < 1).length} ${tr("Devices_PL")})`}</Typography.Text>
            )}
          </Space>
        </Select.Option>
      </Select>
    </Col>
  );
};

MapsProvider.FilterDevicesByModel = function MapsProviderFilterDevicesByModel(
  /** @type {{ selectProps?: import("antd/lib/select").SelectProps<import("../types/global").Garden["id"]>; width?: import("react").CSSProperties["width"] }} */
  { width = "100%", selectProps },
) {
  const { devices } = useDevice();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const MODEL_LIST = ["BL", "LT", "EL", "ELS", "NEM"];

  const DEVICE_MODEL_LABELS = {
    BL: "BLT 9V",
    LT: "SimPro",
    EL: "Gardener",
    ELS: "Farmer",
    NEM: "NemPro",
  };

  // Read models from ?models=BLT,LT
  const selectedModels = useMemo(() => {
    const raw = searchParameters.get("models");
    return raw ? raw.split(",") : [];
  }, [searchParameters]);

  // Count devices per model
  const modelDeviceCounts = useMemo(() => {
    const counts = {};
    MODEL_LIST.forEach((m) => (counts[m] = 0));
    if (devices) {
      devices.forEach((d) => {
        if (MODEL_LIST.includes(d.model)) counts[d.model]++;
      });
    }
    return counts;
  }, [devices]);

  return (
    <Col span={8}>
      <Select
        mode="multiple"
        allowClear
        placeholder={tr("Filter Devices By Model")}
        style={{ width }}
        dropdownMatchSelectWidth={false}
        value={selectedModels}
        {...selectProps}
        onChange={(values) => {
          setSearchParameters((prev) => {
            if (values.length === 0) {
              prev.delete("models");
            } else {
              prev.set("models", values.join(","));
            }
            return prev;
          });
        }}
      >
        {MODEL_LIST.map((model) => (
          <Select.Option key={model} value={model}>
            <Space>
              <Typography.Text>{DEVICE_MODEL_LABELS[model]}</Typography.Text>
              {devices && (
                <Typography.Text type="secondary">
                  ({modelDeviceCounts[model]} {tr("Devices_PL")})
                </Typography.Text>
              )}
            </Space>
          </Select.Option>
        ))}
      </Select>
    </Col>
  );
};

MapsProvider.GardensDevicesByModel = function MapsProviderGardensDevicesByModel() {
  const { devicesByModel } = useDevice();

  if (!devicesByModel) return null;

  return (
    <>
      {
        /** @type {(keyof typeof devicesByModel)[]} */ (Object.keys(devicesByModel)).map((model, index) => {
        const _devices = devicesByModel[model];

        if (!_devices) return null;

        return (
          <LayerGroupRL key={`${model}-${index}`}>
            {_devices.map((device, index) => {
              if (!device.location) return null;

              return (
                <MarkerRL
                  battery={device.battery}
                  batteryPercentage={device.batteryPct}
                  model={device.model}
                  alive={device.alive}
                  icon={getDeviceLocationIcon({ device })}
                  key={`${device.id}-${index}`}
                  position={[device.location?.lat || 41, device.location?.lng || 29]}
                >
                  <PopupRL>
                    <MapsProvider.DevicePopup device={device} />
                  </PopupRL>
                </MarkerRL>
              );
            })}
          </LayerGroupRL>
        );
      })
      }
    </>
  );
};

MapsProvider.GardenDevices = function MapsProviderMapsDeviceMarkers(
  /** @type {{ checked?: boolean; eventHandlers?: import("react-leaflet").FeatureGroupProps["eventHandlers"]; }} */
  { checked = true, eventHandlers },
) {
  const { devices } = useDevice();

  const devicesWithLocation = useMemo(() => devices.filter((d) => d.location), [devices]);

  return (
    <LayersControlRL.Overlay checked={checked} name={tr("Devices")}>
      <LayerGroupRL eventHandlers={eventHandlers}>
        {devicesWithLocation.map((device) => (
          <MarkerRL key={device.id} icon={getDeviceLocationIcon({ device })} position={[device.location?.lat || 0, device.location?.lng || 0]}>
            <PopupRL>
              <MapsProvider.DevicePopup device={device} />
            </PopupRL>
          </MarkerRL>
        ))}
      </LayerGroupRL>
    </LayersControlRL.Overlay>
  );
};

MapsProvider.GardenLayersControl = function MapsProviderLayersControl(
  /** @type {{ children?: import("react").ReactElement<typeof MapsProvider.GardenDevices> | import("react").ReactElement<typeof MapsProvider.GardenDevices>[] | import("react").ReactElement<typeof MapsProvider.GardenAreas> | import("react").ReactElement<typeof MapsProvider.GardenAreas>[]; position?: import("leaflet").ControlPosition; }} */
  { children, position = "topleft" },
) {
  const { fitBoundsToCheckedOverlays } = useMaps();

  useEffect(() => {
    fitBoundsToCheckedOverlays();
  }, [fitBoundsToCheckedOverlays]);

  /**
   * @description Validate children types of MapsProvider.MapsGarden component to ensure only allowed components are used.
   * @throws {Error} If a child is not of type MapsProvider.MapsGardenAreas or MapsProvider.MapsDeviceMarkers.
   */
  if (children) {
    Children.forEach(children, (child) => {
      if (
        isValidElement(child) &&
        child.type !== MapsProvider.DeviceMarker &&
        child.type !== MapsProvider.DeviceOtherMarkers &&
        child.type !== MapsProvider.GardenAreas &&
        child.type !== MapsProvider.GardenDevices &&
        child.type !== MapsProvider.GardensAreas &&
        child.type !== MapsProvider.GardensDevicesByModel &&
        child.type !== MapsProvider.Valves
      )
        throw new Error(
          tr(
            "Only MapsProvider.GardenAreas, MapsProvider.GardenDevices, MapsProvider.GardensAreas, MapsProvider.GardensDevicesByModel, MapsProvider.DeviceMarker, MapsProvider.DeviceOtherMarkers and MapsProvider.Valves components are allowed as children of MapsProvider.GardenLayersControl.",
          ),
        );
    });
  }

  return (
    <LayersControlRL collapsed={false} position={position}>
      {children}
    </LayersControlRL>
  );
};

MapsProvider.GardensLayersControl = function MapsProviderGardensLayerControl(
  /** @type {{ children?: import("react").ReactElement<typeof MapsProvider.GardensAreas> | import("react").ReactElement<typeof MapsProvider.GardensAreas>[]; className?: string; position?: import("leaflet").ControlPosition; }} */
  { children, position = "topleft", ...rest },
) {
  const { fitBoundsToGardens } = useMaps();
  const { gardens } = useGarden();
  const map = useMapRL();
  const [searchParameters] = useSearchParams();

  useEffect(() => {
    if (!gardens) return;

    map.whenReady((e) => {
      const m = e.target;
      const selectedGardenIds = searchParameters.get("selectedGardenId")?.split(",");

      if (selectedGardenIds?.length) {
        const selectedGardens = gardens.filter((g) => selectedGardenIds.includes(g.id.toString()));
        if (selectedGardens.length) {
          fitBoundsToGardens({ gardens: selectedGardens, map: m });
          return;
        }
      } else {
        // close all popups if no garden is selected
        m.closePopup();
      }

      fitBoundsToGardens({ gardens, map: m });
    });
  }, [fitBoundsToGardens, gardens, map, searchParameters]);

  return (
    <CustomControlComponent position={position} {...rest}>
      <Row gutter={[10, 10]}>{children}</Row>
    </CustomControlComponent>
  );
};

MapsProvider.GardenAreas = function MapsProviderGardenAreas(
  /** @type {{ checked?: boolean; editable?: boolean; eventHandlers?: import("react-leaflet").FeatureGroupProps["eventHandlers"]; autoFitBounds?: boolean; }} */
  { checked = true, editable, eventHandlers, autoFitBounds = true },
) {
  const { garden, mutateGarden } = useGarden();
  const map = useMapRL();
  const featureGroupRef = /** @type {import("react").MutableRefObject<L.FeatureGroup | null>} */ (useRef(null));

  /** @type {import("../types/global").handlePolygonEventFunction} */
  const handlePolygonEvent = useCallback(
    (featureGroupRef) => (event) => {
      if (!garden) return;
      if (!featureGroupRef.current) return;

      // Collect all features from the FeatureGroup
      /** @type {Array<import("geojson").Feature<import("geojson").Polygon>>} */
      const allFeatures = [];

      featureGroupRef.current.eachLayer((layer) => {
        /** @type {import("geojson").Feature<import("geojson").Polygon>} */
        const geoJsonFeature = /** @type {any} */ (layer).toGeoJSON();

        if (geoJsonFeature && geoJsonFeature.type === "Feature") {
          allFeatures.push(geoJsonFeature);
        }
      });

      // Detect event type: Created events have 'layer' property
      const isCreatedEvent = "layer" in event;

      // Early return for delete when all polygons removed
      if (!isCreatedEvent && allFeatures.length === 0) {
        console.warn("All polygons deleted - keeping existing garden state");
        return;
      }

      // Process based on detected event type
      /** @type {import("geojson").Feature<import("geojson").Polygon | import("geojson").MultiPolygon>} */
      let processedResult;

      if (isCreatedEvent) {
        // For created events, use turf.union to merge polygons
        const unionResult = turf.union(turf.featureCollection(allFeatures));
        if (!unionResult) throw new Error("Turf union result is undefined");
        processedResult = unionResult;
      } else {
        // For edited and deleted events, use turf.combine to group polygons
        /** @type {import("geojson").FeatureCollection<import("geojson").Polygon | import("geojson").MultiPolygon>} */
        const combined = /** @type {any} */ (turf.combine(turf.featureCollection(allFeatures)));

        if (!combined.features || combined.features.length === 0) {
          throw new Error("Failed to combine features");
        }

        processedResult = combined.features[0];
      }

      // Convert result to MultiPolygon format for database consistency
      /** @type {import("../types/global").GeoJSONFeature} */
      let updatedGeoJson;

      switch (processedResult.geometry.type) {
        case "Polygon":
          // Convert single Polygon to MultiPolygon by wrapping coordinates in an array
          updatedGeoJson = /** @type {import("../types/global").GeoJSONFeature} */ ({
            type: "Feature",
            geometry: {
              type: "MultiPolygon",
              coordinates: [/** @type {import("geojson").Position[][]} */ (processedResult.geometry.coordinates)],
            },
            properties: {
              ...garden.geoJson.properties,
              lastModified: new Date().toISOString(),
            },
          });
          break;

        case "MultiPolygon":
          // Already MultiPolygon, just preserve properties
          updatedGeoJson = /** @type {import("../types/global").GeoJSONFeature} */ ({
            type: "Feature",
            geometry: processedResult.geometry,
            properties: {
              ...garden.geoJson.properties,
              lastModified: new Date().toISOString(),
            },
          });
          break;

        default:
          throw new Error(tr("Unexpected geometry type"));
      }

      // Update the garden with the new GeoJSON via optimistic mutation
      mutateGarden({ id: garden.id, geoJson: updatedGeoJson });
    },
    [garden, mutateGarden],
  );

  useEffect(() => {
    if (!garden?.geoJson) return;
    if (!featureGroupRef.current) return;
    if (!map) return;

    const featureGroup = featureGroupRef.current;

    featureGroup.clearLayers();

    const flattened = turf.flatten(garden.geoJson);

    /** @type {import("geojson").Feature[]} */
    const validFeatures = flattened.features.filter(isRenderableGeoJSONFeature);

    if (validFeatures.length === 0) {
      console.warn("GardenAreas: no valid polygon geometry");
      return;
    }

    L.geoJSON(
      {
        type: "FeatureCollection",
        features: validFeatures,
      },
      {
        interactive: true,
        onEachFeature: (_, layer) => {
          featureGroup.addLayer(layer);
        },
        style: {
          fillColor: getGardenAreaColor(garden),
          fillOpacity: 0.5,
          color: getGardenBorderColor(garden),
          stroke: true,
        },
      },
    );

    if (!autoFitBounds) return;

    const bounds = featureGroup.getBounds();

    if (!boundsHaveArea(bounds)) return;

    // Delay to avoid race with Leaflet Draw init
    const id = requestAnimationFrame(() => {
      map.fitBounds(bounds, { maxZoom: 18 });
    });

    return () => cancelAnimationFrame(id);
  }, [garden, map, autoFitBounds]);

  if (process.env.NODE_ENV === "development") window.map = map;
  if (process.env.NODE_ENV === "development") window.turf = turf;

  if (!garden) return null;

  return (
    <LayersControlRL.Overlay checked={checked} name={tr("Garden Area")}>
      <FeatureGroupRL eventHandlers={eventHandlers} ref={featureGroupRef}>
        {editable && (
          <EditControl
            draw={{ circle: false, circlemarker: false, marker: false, polyline: false, polygon: true, rectangle: false }}
            onCreated={handlePolygonEvent(featureGroupRef)}
            onDeleted={handlePolygonEvent(featureGroupRef)}
            onEdited={handlePolygonEvent(featureGroupRef)}
            position="topleft"
          />
        )}
      </FeatureGroupRL>
    </LayersControlRL.Overlay>
  );
};

MapsProvider.GardenPopover = function MapsProviderGardenPopoever(/** @type {{ garden: import("../types/global").Garden }} */ { garden }) {
  const navigate = useNavigate();

  const { data } = useQuery(["Devices", garden.id], () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${garden.id}/device`), {
    staleTime: 5 * 60 * 1000,
    enabled: !!garden.id,
  });

  const devices = data?.data?.devices ?? [];

  return (
    <div style={{ width: 320, maxWidth: "100%" }}>
      <Row align="top" justify="space-between">
        <Col flex="auto">
          <Space>
            <Space align="center" size={8} style={{ marginBottom: 4 }}>
              <Typography.Title level={5} style={{ lineHeight: 1, margin: 0, whiteSpace: "wrap" }} ellipsis={{ tooltip: garden.name }}>
                {garden.name}
              </Typography.Title>
              <Tag color={garden.status === "active" ? "success" : "default"} style={{ margin: 0 }}>
                {garden.status === "active" ? tr("Active") : tr("Passive")}
              </Tag>
            </Space>

            {garden.optimization?.active && (
              <Space size={8} style={{ color: "#8c8c8c", fontSize: 14, marginTop: 4 }}>
                {garden.optimization.sun && (
                  <Tooltip title={tr("Sun Optimization Active")}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <FaSun style={{ color: "#fadb14" }} />
                    </span>
                  </Tooltip>
                )}

                {garden.optimization?.active && garden.optimization?.rain && (
                  <Tooltip title={tr("Rain Optimization Active")}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <FaCloudRain style={{ color: "#69c0ff" }} />
                    </span>
                  </Tooltip>
                )}

                {garden.optimization?.active && garden.optimization?.wind && (
                  <Tooltip title={tr("Wind Optimization Active")}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <FaWind style={{ color: "#bfbfbf" }} />
                    </span>
                  </Tooltip>
                )}
              </Space>
            )}
          </Space>
        </Col>
      </Row>

      <Divider style={{ margin: "12px 0" }} />

      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        <List
          size="small"
          dataSource={devices}
          split={false}
          renderItem={(device) => {
            const activeValves = Object.values(device.valves || {}).filter((v) => v.state === "on").length;
            const isOnline = device.alive === "alive";

            return (
              <List.Item style={{ padding: "8px 0" }}>
                <Row align="middle" justify="space-between" style={{ width: "100%" }}>
                  <Col style={{ overflow: "hidden", flex: 1 }}>
                    <Space align="center" size={12} style={{ width: "100%", overflow: "hidden" }}>
                      <Tooltip title={isOnline ? tr("Online") : tr("Offline")}>
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <img
                            src={deviceImageMap[device.model]}
                            alt={device.model}
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "contain",
                              filter: isOnline ? "none" : "grayscale(100%) opacity(0.6)",
                            }}
                          />
                          <Badge
                            status={isOnline ? "success" : "default"}
                            style={{
                              position: "absolute",
                              bottom: -4,
                              right: -4,
                              boxShadow: "0 0 0 2px #fff",
                              borderRadius: "50%",
                            }}
                          />
                        </div>
                      </Tooltip>

                      <Space direction="vertical" size={0}>
                        <Typography.Text strong ellipsis style={{ fontSize: 14 }}>
                          {device.name || device.deviceSerial}
                        </Typography.Text>

                        {activeValves > 0 && (
                          <Tooltip title={tr("Number of Active Valves")}>
                            <Tag color="processing" style={{ margin: 0, fontSize: 12, border: 0 }}>
                              {activeValves} {tr("Active Valve(s)")}
                            </Tag>
                          </Tooltip>
                        )}
                      </Space>
                    </Space>
                  </Col>

                  <Col style={{ marginLeft: 8 }}>
                    <Space align="center" size={8}>
                      <DeviceProvider.DeviceSignal device={device} />
                      <DeviceProvider.DeviceBattery device={device} />
                      <DeviceProvider.DeviceStatus device={device} />
                    </Space>
                  </Col>
                </Row>
              </List.Item>
            );
          }}
        />
        {garden.devices.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tr("No devices found")} />}
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <AccessGate role={ROLES.END_USER}>
        <Button type="primary" block onClick={() => navigate(`${garden.id}`)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {tr("View Garden Details")}
        </Button>
      </AccessGate>
    </div>
  );
};

MapsProvider.GardensAreas = function MapsProviderGardensAreas() {
  const { gardens } = useGarden();
  const { width: windowWidth } = useWindowSize();
  const [isFilterExpanded] = useMaps().isFilterExpandedState;
  const [searchParameters] = useSearchParams();
  const { id: routeGardenId } = useParams();

  const selectedGardenIds = useMemo(() => {
    if (routeGardenId) return [routeGardenId];
    const value = searchParameters.get("selectedGardenId");
    return value ? value.split(",").filter(Boolean) : [];
  }, [searchParameters, routeGardenId]);

  if (!gardens) return null;

  const visibleGardens = selectedGardenIds.length ? gardens.filter((garden) => selectedGardenIds.includes(garden.id.toString())) : gardens;

  if (process.env.NODE_ENV === "development") {
    window.gardens = gardens;
  }

  return (
    <>
      {visibleGardens.map((garden) => {
        if (!garden.geoJson) return null;

        const flattenedGeoJson = turf.flatten(garden.geoJson);

        /** @type {import("geojson").Feature[]} */
        const validFeatures = flattenedGeoJson.features.filter(isRenderableGeoJSONFeature);
        if (validFeatures.length === 0) return null;
        return (
          <FeatureGroupRL
            eventHandlers={{
              click: (event) => {
                const layer = event.target;
                layer.openPopup();
                const bounds = layer.getBounds();

                if (bounds.isValid()) {
                  /** @type {import("leaflet").FitBoundsOptions} */
                  const options = windowWidth && windowWidth >= 768 ? { paddingTopLeft: [320, isFilterExpanded ? 320 : 0] } : {};
                  layer._map.fitBounds(bounds, options);
                }
              },
            }}
            id={garden.id.toString()}
            key={garden.id}
            type="garden"
          >
            <PopupRL maxWidth={350} minWidth={300}>
              <MapsProvider.GardenPopover garden={garden} />
            </PopupRL>

            <GeoJSONRL
              data={flattenedGeoJson}
              style={{
                fillColor: getGardenAreaColor(garden),
                fillOpacity: 0.5,
                color: getGardenBorderColor(garden),
                stroke: true,
              }}
            />
          </FeatureGroupRL>
        );
      })}
    </>
  );
};

MapsProvider.GardensListSelect = function MapsProviderGardensList(
  /** @type {{ selectProps?: import("antd/lib/select").SelectProps<import("../types/global").Garden["id"]>; width?: import("react").CSSProperties["width"]; }} */
  { selectProps, width = "100%" },
) {
  const { gardens } = useGarden();
  const { isGardensSelectListOpenState } = useMaps();
  const [isGardensSelectListOpen, setIsGardensSelectListOpen] = isGardensSelectListOpenState;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [selectGardens, setSelectGardens] = useState(/** @type {import("../types/global").Garden[]} */([]));
  const truncate = (/** @type {string} */ text, max = 30) => (text.length > max ? `${text.slice(0, max)}…` : text);

  useEffect(() => {
    if (gardens) {
      setSelectGardens(gardens);
    }
  }, [gardens]);

  if (!gardens) return null;

  return (
    <Col flex="0 0 300px" style={{ minWidth: 300, maxWidth: 300 }}>
      <Select
        {...selectProps}
        style={{ width: "100%", minWidth: 300 }}
        allowClear
        autoFocus
        dropdownRender={(menu) => (
          <div>
            {menu}

            <Divider orientation="right" orientationMargin={10} style={{ marginBottom: 10, marginTop: 0 }} />

            <Space wrap={true} style={{ paddingInline: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {tr("Legend")}:
              </Typography.Text>

              <Tooltip arrowPointAtCenter={true} placement="bottomLeft" title={tr("Garden Is Passive")}>
                <div className="w-3 h-3 bg-red-300 mr-1 border border-gray-300 rounded"></div>
              </Tooltip>

              <Tooltip arrowPointAtCenter={true} placement="bottomLeft" title={tr("No Active Plans")}>
                <div className="w-3 h-3 bg-green-300 mr-1 border border-green-300 rounded"></div>
              </Tooltip>

              <Tooltip arrowPointAtCenter={true} placement="bottomLeft" title={tr("Has Upcoming Plan")}>
                <div className="w-3 h-3 bg-blue-200 mr-1 border border-gray-300 rounded"></div>
              </Tooltip>

              <Tooltip arrowPointAtCenter={true} placement="bottomLeft" title={tr("Has No Upcoming Plans")}>
                <div className="w-3 h-3 bg-yellow-300 mr-1 border border-gray-300 rounded"></div>
              </Tooltip>
            </Space>

            <Divider orientation="right" orientationMargin={10} style={{ marginBottom: 6, marginTop: 3 }}>
              <Button
                onClick={(e) => {
                  // stopPropagation to prevent immediate reopen
                  e.stopPropagation();

                  setIsGardensSelectListOpen(false);
                }}
                size="small"
                type="link"
              >
                {tr("Hide Garden List")}
              </Button>
            </Divider>
          </div>
        )}
        filterOption={false}
        notFoundContent={
          <Empty
            image={<BsFillPinMapFill style={{ fontSize: 36 }} />}
            imageStyle={{ height: 36, marginTop: 36 }}
            description={
              <Typography.Paragraph style={{ lineHeight: 1.1, marginBottom: 36 }} type="secondary">
                {tr("No gardens found")}
              </Typography.Paragraph>
            }
          />
        }
        onChange={(value) => {
          setSearchParameters((prev) => {
            if (!value) {
              prev.delete("selectedGardenId");
              return prev;
            }

            prev.set("selectedGardenId", value);
            return prev;
          });
        }}
        onClear={() => {
          setSearchParameters((prev) => {
            prev.delete("selectedGardenId");
            return prev;
          });
        }}
        onFocus={() => setIsGardensSelectListOpen(true)}
        onSearch={(value) => {
          if (!value || value.trim() === "") {
            setSelectGardens(gardens);
            return;
          }

          setSelectGardens((g) => {
            /** @type {import("../types/global").Garden[]} */
            const filteredGardens = gardens.filter((garden) => {
              const v = value.toLowerCase();
              return garden.name.toLowerCase().includes(v);
            });

            return filteredGardens;
          });
        }}
        open={isGardensSelectListOpen}
        options={selectGardens
          .filter((garden) => {
            const tagsFilter = searchParameters.get("tags");
            if (!tagsFilter) return true;

            return garden.tags.some((tag) => searchParameters.get("tags")?.split(",").includes(tag));
          })
          .map((garden) => ({
            label: (
              <Space size={6} style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
                <Space size={4}>
                  <MapsProvider.GardenStatusLegend garden={garden} />
                  <Typography.Text
                    ellipsis={{
                      tooltip: {
                        title: garden.name,
                        placement: "right",
                        align: {
                          offset: [60, 0],
                        },
                      },
                    }}
                    style={{ maxWidth: 180 }}
                  >
                    {garden.name}
                  </Typography.Text>
                </Space>
                <MapsProvider.GardenOptions garden={garden} />
              </Space>
            ),
            value: garden.id,
            valueLabel: (
              <span style={{ display: "inline-block", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {truncate(garden.name)}
              </span>
            ),
          }))}
        placeholder={tr("Park/Garden Search")}
        optionLabelProp="valueLabel"
        showSearch
        // @ts-ignore
        listHeight={window.innerHeight - 210}
        style={{ width }}
        defaultValue={searchParameters.get("selectedGardenId") || undefined}
      />
    </Col>
  );
};

MapsProvider.GardenStatusLegend = function MapsProviderGardenStatusLegend(
  /** @type {import("react").PropsWithChildren<{garden:import("../types/global").Garden}>} */ { garden },
) {
  if (garden.wateringStatus === "disabled") {
    // Garden passive
    return <div className="w-3 h-3 bg-red-300 mr-1 border border-gray-300 rounded"></div>;
  } else {
    //Garden Active
    if (garden.devices.every((d) => d.plans.filter((p) => p.status === "active").length === 0)) {
      //No active plan
      return <div className="w-3 h-3 bg-green-300 mr-1 border border-green-300 rounded"></div>;
    }

    if (garden.next24h && garden.next24h.length === 0) {
      //No Upcoming plan
      return <div className="w-3 h-3 bg-yellow-300 mr-1 border border-gray-300 rounded"></div>;
    }

    if (garden.next24h && garden.next24h.length > 0) {
      //Upcoming plan
      return <div className="w-3 h-3 bg-blue-200 mr-1 border border-gray-300 rounded"></div>;
    }

    return <div className="w-3 h-3 bg-gray-50 mr-1 border border-gray-300 rounded"></div>;
  }
};

MapsProvider.GardenOptions = function MapsProviderGardenOptions(/** @type {import("react").PropsWithChildren<{garden:import("../types/global").Garden}>} */ { garden }) {
  const navigate = useNavigate();
  const { isMutatingGarden, mutateGarden } = useGarden();

  return (
    <AccessGate role={ROLES.END_USER}>
      <Row align="middle" gutter={8}>
        <Tooltip title={tr("Watering Status")}>
          <Switch
            checked={garden?.wateringStatus === "active"}
            disabled={isMutatingGarden}
            onChange={async (checked) => await mutateGarden({ id: garden.id, wateringStatus: checked ? "active" : "disabled" })}
            size="small"
            style={{ transform: "scale(0.85)" }}
          />
        </Tooltip>
        <Tooltip title={tr("Go to garden")}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              // Prevent Select option click
              e.stopPropagation();

              navigate(`/control-center/gardens/${garden.id}`);
            }}
            style={{
              marginLeft: "auto",
              paddingInline: 4,
              height: 24,
            }}
          />
        </Tooltip>
      </Row>
    </AccessGate>
  );
};

MapsProvider.BatchOptimizationButton = function MapsProviderBatchOptimizationButton(
  /** @type {import("react").PropsWithChildren<{ position?: import("leaflet").ControlPosition }>} */
  { children, position = "topright", ...props },
) {
  return (
    <AccessGate role={ROLES.END_USER}>
      <CustomControlComponent position={position} {...props}>
        {children}
      </CustomControlComponent>
    </AccessGate>
  );
};

MapsProvider.SelectBaseMap = function MapsProviderSelectBaseMap(
  /** @type {{ className?: string; defaultMapLabel?: import("../types/global").BaseMap["label"]; disablePropagation?: boolean; selectProps?: import("antd/lib/select").SelectProps<import("../types/global").Garden["id"]>; style?: React.CSSProperties; position: import("leaflet").ControlPosition; width?: import("react").CSSProperties["width"] }} */
  { defaultMapLabel, width = 200, selectProps, ...props },
) {
  const { baseMapLabelState } = useMaps();
  const [baseMapLabel, setBaseMapLabel] = baseMapLabelState;
  const [locale] = useState(localStorage.getItem("locale") || "tr");

  return (
    <CustomControlComponent {...props}>
      <Select dropdownStyle={{ zIndex: 1054 }} onChange={setBaseMapLabel} value={baseMapLabel} style={{ width }} {...selectProps}>
        {BASE_MAPS.map((m) => (
          <Select.Option key={m.label} value={m.label}>
            {locale === "tr" ? m.label : m.enLabel}
          </Select.Option>
        ))}
      </Select>
    </CustomControlComponent>
  );
};

MapsProvider.TileLayer = function MapsProviderMapTile(
  /** @type {Omit<import("react-leaflet").TileLayerProps, "url"> & { defaultBaseMapLabel?: import("../types/global").BaseMap["label"]; url?: import("react-leaflet").TileLayerProps["url"] }} */
  { defaultBaseMapLabel, url, ...tileLayerProps },
) {
  const tileLayerRef = useRef(/** @type {L.TileLayer | null} */(null));
  const [baseMapLabel, setBaseMapLabel] = useMaps().baseMapLabelState;

  const baseMapUrl = useMemo(() => {
    const baseMap = BASE_MAPS.find((m) => m.label === baseMapLabel);
    if (!baseMap) throw new Error(tr("Base map URL not found"));

    return baseMap.value;
  }, [baseMapLabel]);

  useEffect(() => {
    if (!defaultBaseMapLabel) return;

    setBaseMapLabel(defaultBaseMapLabel);
  }, [defaultBaseMapLabel, setBaseMapLabel]);

  return <TileLayerRL {...tileLayerProps} maxNativeZoom={55} maxZoom={80} ref={tileLayerRef} url={baseMapUrl} />;
};

MapsProvider.ExpandFilterIconButton = function MapsProviderExpandFilterIconButton() {
  const [isExpanded, setIsExpanded] = useMaps().isFilterExpandedState;
  const [, setIsGardensSelectListOpen] = useMaps().isGardensSelectListOpenState;
  const [searchParameters] = useSearchParams();
  const { width: windowWidth } = useWindowSize();
  const batteryPercentage =
    searchParameters
      .get("batteryPercentage")
      ?.split(",")
      .filter((i) => i !== "") || [];
  const tags =
    searchParameters
      .get("tags")
      ?.split(",")
      .filter((i) => i !== "") || [];

  if (isExpanded) return null;

  return (
    <Col>
      <Badge count={Number(tags.length + batteryPercentage.length)} size="small">
        <Tooltip title={tr("Open Filters")}>
          <Button
            icon={<FilterOutlined />}
            onClick={() => {
              setIsExpanded((ie) => !ie);

              if (windowWidth && windowWidth < 768) {
                setIsGardensSelectListOpen(false);
              }
            }}
            shape="circle"
            type="primary"
          />
        </Tooltip>
      </Badge>
    </Col>
  );
};

MapsProvider.ManageGardenOptimizationsButton = function MapsProviderManageGardenOptimizationsButton() {
  const [, setSearchParameters] = useSearchParams();

  return (
    <OptimizationProvider>
      <Col>
        <Tooltip placement="left" title={tr("Optimization Settings")}>
          <Button
            onClick={() => {
              setSearchParameters((prev) => {
                prev.set("gardens_optimizations", "true");
                return prev;
              });
            }}
            shape="circle"
            type="primary"
          >
            <Space style={{ display: "flex", alignItems: "center", paddingInline: 15 }}>
              <OptimizationProvider.Icon color="white" optimizationName="rain" />
              <OptimizationProvider.Icon color="white" optimizationName="sun" />
              <OptimizationProvider.Icon color="white" optimizationName="wind" />
            </Space>
          </Button>
        </Tooltip>
      </Col>
    </OptimizationProvider>
  );
};

MapsProvider.BatchWateringStatusButton = function MapsProviderBatchWateringStatusButton() {
  const [, setSearchParameters] = useSearchParams();
  return (
    <Col>
      <Tooltip placement="left" title={tr("Toggle Watering Status")}>
        <Button
          icon={<FaTint size={16} />}
          onClick={() => {
            setSearchParameters((prev) => {
              prev.set("batch_watering", "true");
              return prev;
            });
          }}
          shape="circle"
          style={{ alignItems: "center", display: "flex", justifyContent: "center" }}
          type="primary"
        />
      </Tooltip>
    </Col>
  );
};

MapsProvider.AddGardenButton = function MapsProviderAddGardenButton() {
  const [isExpanded] = useMaps().isFilterExpandedState;
  const [, setSearchParameters] = useSearchParams();

  if (isExpanded) return null;

  return (
    <AccessGate role={ROLES.FIELD_MANAGER}>
      <Col>
        <Tooltip title={tr("Add New Garden")}>
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setSearchParameters((prev) => {
                prev.delete("selectedGardenId");
                prev.set("add_garden", "true");
                return prev;
              });
            }}
            shape="circle"
            type="primary"
          />
        </Tooltip>
      </Col>
    </AccessGate>
  );
};

MapsProvider.GardenOrderModal = function MapsProviderGardenOrderModal() {
  const { gardens, fetchAllGardens } = useGarden();
  const queryClient = useQueryClient();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const isOpen = searchParameters.get("order_gardens") === "true";

  const [orderedGardens, setOrderedGardens] = useState(
    /** @type {import("../types/global").Garden[]} */([]),
  );
  const [isDirty, setIsDirty] = useState(false);

  // Seed local order from server whenever the modal opens or data changes
  useEffect(() => {
    if (isOpen && gardens) {
      setOrderedGardens(gardens);
      setIsDirty(false);
    }
  }, [isOpen, gardens]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const closeModal = () => {
    setSearchParameters((prev) => {
      prev.delete("order_gardens");
      return prev;
    });
  };

  const onDragEnd = (/** @type {import("@dnd-kit/core").DragEndEvent} */ event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedGardens((items) => {
      const oldIndex = items.findIndex((g) => g.id === active.id);
      const newIndex = items.findIndex((g) => g.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
    setIsDirty(true);
  };

  const updateGardenOrder = useMutation(
    (/** @type {string[]} */ gardenIds) =>
      axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/order`, {
        order: gardenIds,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Gardens"]);
        notification.success({ message: tr("Garden order saved successfully") });
        closeModal();
      },
      onError: () => {
        notification.error({ message: tr("Failed to save garden order") });
      },
    },
  );

  const onSave = () => {
    updateGardenOrder.mutate(orderedGardens.map((g) => g._id));
  };

  return (
    <Modal
      title={tr("Order Gardens")}
      open={isOpen}
      onCancel={closeModal}
      footer={null}  // we manage actions ourselves
      zIndex={2000}
      width={520}
      styles={{ body: { padding: 0 } }}
      destroyOnClose
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "#fff",
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {tr("Drag gardens to reorder (Favorites will always appear at the top)")}
        </Typography.Text>
        <Space>
          <Button onClick={closeModal}>{tr("Cancel")}</Button>
          <Button
            type="primary"
            onClick={onSave}
            loading={updateGardenOrder.isLoading}
            disabled={!isDirty}
          >
            {tr("Save Order")}
          </Button>
        </Space>
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "8px 16px" }}>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={orderedGardens.map((g) => g.id)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {orderedGardens.map((garden, index) => (
                <MapsProvider.SortableGardenRow
                  key={garden.id}
                  garden={garden}
                  index={index}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </Modal>
  );
};

MapsProvider.SortableGardenRow = function MapsProviderSortableGardenRow(
  /** @type {{ garden: import("../types/global").Garden; index: number }} */
  { garden, index },
) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: garden.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-2 border rounded-md bg-white"
    >
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: "grab", display: "flex", touchAction: "none" }}
        aria-label={tr("Drag to reorder")}
      >
        <HolderOutlined style={{ fontSize: 16, color: "#9ca3af" }} />
      </span>

      <Typography.Text type="secondary" style={{ width: 24, fontSize: 12 }}>
        {index + 1}
      </Typography.Text>

      <MapsProvider.GardenStatusLegend garden={garden} />

      <Typography.Text ellipsis={{ tooltip: garden.name }} style={{ flex: 1 }}>
        {garden.name}
      </Typography.Text>
    </div>
  );
};

MapsProvider.OrderGardensButton = function MapsProviderOrderGardensButton() {
  const [isExpanded] = useMaps().isFilterExpandedState;
  const [, setSearchParameters] = useSearchParams();

  if (isExpanded) return null;

  return (
    <AccessGate role={ROLES.FIELD_MANAGER}>
      <Col>
        <Tooltip title={tr("Order Gardens")}>
          <Button
            icon={<OrderedListOutlined />}
            onClick={() => {
              setSearchParameters((prev) => {
                prev.set("order_gardens", "true");
                return prev;
              });
            }}
            shape="circle"
            type="primary"
          />
        </Tooltip>
      </Col>
    </AccessGate>
  );
};

MapsProvider.ShrinkFilterIconButton = function MapsProviderExpandFilterIconButton(
  /** @type { import("antd").ButtonProps } */
  { shape = "circle", type = "primary", ...rest },
) {
  const [isExpanded, setIsExpanded] = useMaps().isFilterExpandedState;

  if (!isExpanded) return null;

  return (
    <Tooltip title={tr("Close Filters")} placement="bottomRight">
      <Button icon={<CloseOutlined />} onClick={() => setIsExpanded((ie) => !ie)} shape={shape} type={type} {...rest} />
    </Tooltip>
  );
};

MapsProvider.FilterGardensByTags = function MapsProviderFilterGardensByTags() {
  const { tags, isFetchingTags } = useMaps();
  const { gardens } = useGarden();
  const [searchParameters, setSearchParameters] = useSearchParams();

  if (!tags) return null;
  if (!tags.length) return null;

  return (
    <Col span={8}>
      <Select
        allowClear
        disabled={isFetchingTags}
        loading={isFetchingTags}
        mode="tags"
        onChange={(value) => {
          setSearchParameters((prev) => {
            // append to "tags" search parameter
            const existingTags = prev.get("tags")?.split(",") || [];
            if (typeof value === "string") {
              if (!existingTags.includes(value)) {
                existingTags.push(value);
              }
            }

            if (Array.isArray(value)) {
              // remove tags that are not in value
              const newTags = existingTags.filter((tag) => value.includes(tag));
              // add new tags from value
              value.forEach((tag) => {
                if (!newTags.includes(tag)) {
                  newTags.push(tag);
                }
              });
              existingTags.length = 0;
              existingTags.push(...newTags);
            }

            if (existingTags.length) {
              prev.set("tags", existingTags.join(","));
            } else {
              prev.delete("tags");
            }

            if (gardens && existingTags.length) {
              const selectedGardenIds = gardens.filter((garden) => garden.tags?.some((tag) => existingTags.includes(tag))).map((garden) => garden.id.toString());

              if (selectedGardenIds.length) {
                prev.set("selectedGardenId", selectedGardenIds.join(","));
              } else {
                prev.delete("selectedGardenId");
              }
            } else {
              prev.delete("selectedGardenId");
            }
            return prev;
          });
        }}
        options={tags.map((tag) => ({ label: tr(tag.name), value: tag.id }))}
        placeholder={tr("Filter gardens by tags")}
        style={{ maxWidth: "100%", width: "100%" }}
        showSearch
        value={searchParameters.get("tags")?.split(",") || []}
      />
    </Col>
  );
};

MapsProvider.Valves = function MapsProviderValves(
  /**
   * @type {{
   * callback?: (feature: import("geojson").Feature) => void,
   * device: import("../types/device").Device,
   * activeValveId?: string | number,
   * onActiveValveChange?: (valveId: string | null) => void,
   * }} */
  { callback = () => { }, device, activeValveId: controlledActiveValveId, onActiveValveChange },
) {
  const map = useMapRL();
  const { mutateUpdateValveArea } = useDevice();
  const { activeValveIdState, setActiveValveId: setContextActiveValveId } = useMaps();
  const [contextActiveValveId] = activeValveIdState;
  const activeValveId = controlledActiveValveId !== undefined ? controlledActiveValveId : contextActiveValveId;
  const activeValveIdRef = /** @type {import("react").MutableRefObject<string | null>} */ (useRef(activeValveId ? String(activeValveId) : null));
  const setActiveValveId = useCallback(
    /** @param {string | null} valveId */
    (valveId) => {
      setContextActiveValveId(valveId);

      if (typeof onActiveValveChange === "function") {
        onActiveValveChange(valveId);
      }
    },
    [onActiveValveChange, setContextActiveValveId],
  );

  useEffect(() => {
    activeValveIdRef.current = activeValveId ? String(activeValveId) : null;
  }, [activeValveId]);

  const featureGroupRef = /** @type {import("react").MutableRefObject<L.FeatureGroup | null>} */ (useRef(null));
  const valves = /** @type {Array<{ valveId: string; feature: import("geojson").Feature }>} */ useMemo(() => {
    const valveAreas = /** @type {Record<string, unknown> | undefined} */ (/** @type {unknown} */ (device.valves));
    const source = valveAreas || device.valves;

    if (!source) return [];

    return Object.entries(source).reduce((acc, [valveId, valve]) => {
      const valveAsAny = /** @type {{ geoJson?: unknown; state?: string }} */ (/** @type {unknown} */ (valve));
      const sourceFeature = /** @type {import("geojson").Feature | null} */ (valveAsAny.geoJson || valve);
      const stateFromDeviceValves = /** @type {{ state?: string } | undefined} */ (/** @type {unknown} */ (device.valves?.[valveId]));

      if (!sourceFeature || sourceFeature.type !== "Feature") return acc;
      if (!isRenderableGeoJSONFeature(sourceFeature)) return acc;

      acc.push({
        valveId,
        feature: {
          ...sourceFeature,
          properties: {
            ...(sourceFeature.properties || {}),
            valveId,
            state: stateFromDeviceValves?.state || valveAsAny.state,
          },
        },
      });

      return acc;
    }, /** @type {Array<{ valveId: string; feature: import("geojson").Feature }>} */([]));
  }, [device.valves]);

  const getFeatureFromGeoJSON = useCallback((/** @type {import("geojson").GeoJSON} */ geoJson) => {
    if (!geoJson) return null;

    if (geoJson.type === "Feature") {
      return isRenderableGeoJSONFeature(geoJson) ? geoJson : null;
    }

    if (geoJson.type === "FeatureCollection") {
      const firstRenderableFeature = geoJson.features.find(isRenderableGeoJSONFeature);
      return firstRenderableFeature || null;
    }

    return null;
  }, []);

  /**
   * @param {import("geojson").Feature | null} feature
   * @returns {number[][][] | number[][][][] | null}
   */
  const getCoordinatesFromFeature = useCallback((feature) => {
    if (!feature?.geometry) return null;

    const geometry = feature.geometry;

    if (geometry.type === "Polygon") {
      return /** @type {number[][][]} */ (geometry.coordinates);
    }

    if (geometry.type === "MultiPolygon") {
      return /** @type {number[][][][]} */ (geometry.coordinates);
    }

    return null;
  }, []);

  const handlePolygonEvent = useCallback(
    (/** @type {any} */ event) => {
      const resolvedValveId = activeValveIdRef.current;
      if (!resolvedValveId) {
        console.warn("No active valve selected while updating valve area.");
        return;
      }

      const numericValveId = Number(resolvedValveId);
      if (Number.isNaN(numericValveId)) {
        console.warn("Active valve id is not numeric:", resolvedValveId);
        return;
      }

      const isDeleteEvent = !!(event && "layers" in event && event.layers && typeof event.layers.getLayers === "function" && !("layer" in event));

      if (isDeleteEvent) {
        mutateUpdateValveArea({
          deviceSerial: device.deviceSerial,
          valveId: numericValveId,
          coordinates: [],
        });

        return;
      }

      const featureGroup = featureGroupRef.current;
      if (!featureGroup) return;

      /** @type {import("geojson").Feature | null} */
      let nextFeature = null;

      if (event && "layer" in event && event.layer && typeof event.layer.toGeoJSON === "function") {
        const createdGeoJson = /** @type {import("geojson").GeoJSON} */ (event.layer.toGeoJSON());
        nextFeature = getFeatureFromGeoJSON(createdGeoJson);
      }

      if (!nextFeature && event && "layers" in event && event.layers && typeof event.layers.eachLayer === "function") {
        event.layers.eachLayer((/** @type {{ toGeoJSON?: () => import("geojson").GeoJSON }} */ layer) => {
          if (nextFeature) return;
          if (!layer || typeof layer.toGeoJSON !== "function") return;

          nextFeature = getFeatureFromGeoJSON(layer.toGeoJSON());
        });
      }

      if (!nextFeature) {
        const featureCollectionOfPolygons = featureGroup.toGeoJSON();
        nextFeature = getFeatureFromGeoJSON(featureCollectionOfPolygons);
      }

      if (!nextFeature) return;

      const coordinates = getCoordinatesFromFeature(nextFeature);

      if (!coordinates || Number.isNaN(numericValveId)) return;

      mutateUpdateValveArea({
        deviceSerial: device.deviceSerial,
        valveId: numericValveId,
        coordinates,
      });

      callback(nextFeature);

      console.log("Polygon event processed, next feature:", nextFeature);
    },
    [callback, device.deviceSerial, getCoordinatesFromFeature, getFeatureFromGeoJSON, mutateUpdateValveArea],
  );

  useEffect(() => {
    console.count("Active Valve Change Effect Triggered");
    if (!map) return;

    map.whenReady(() => {
      map.invalidateSize();
    });
  }, [activeValveId, map]);

  console.log("Rendering Valves with features:", activeValveId, valves);

  const drawValveId = activeValveId;
  const hasActiveValve = activeValveId;
  const drawValveColors = getValveGroupColors(drawValveId, drawValveId);

  return (
    <LayersControlRL.Overlay checked={true} name={tr("Valves")}>
      <FeatureGroupRL ref={featureGroupRef}>
        <EditControl
          draw={{
            circle: false,
            circlemarker: false,
            marker: false,
            polyline: false,
            polygon: {
              shapeOptions: {
                fillColor: drawValveColors.fillColor,
                fillOpacity: 0.5,
                color: drawValveColors.color,
                stroke: true,
              },
            },
            rectangle: false,
          }}
          // edit={{ selectedPathOptions: { color: drawValveColors.color, fillColor: drawValveColors.fillColor, fillOpacity: 0.5, stroke: true } }}
          onCreated={handlePolygonEvent}
          onDeleted={handlePolygonEvent}
          onEdited={handlePolygonEvent}
          position="topright"
        />

        {valves.map(({ valveId, feature }) => {
          const { fillColor, color } = getValveGroupColors(valveId, activeValveId);

          return (
            <PolygonRL
              eventHandlers={{
                click: () => setActiveValveId(valveId),
              }}
              key={valveId}
              pathOptions={{
                fillColor,
                fillOpacity: 0.5,
                color,
                stroke: true,
              }}
              positions={geoJSONPolygonToLeafletPositions(/** @type {import("geojson").Polygon | import("geojson").MultiPolygon} */(feature.geometry))}
            />
          );
        })}
      </FeatureGroupRL>
    </LayersControlRL.Overlay>
  );
};
